import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {Disposers} from '../../../../../../lib/helpers';
import {X9ContentStateStore} from '../../../common/X9ContentStateStore';
import {SyncedAudioModel} from '../../models';
import {AudioElementStore} from '../common/AudioElementStore';
import {AudioProgressStore} from '../common/AudioProgressStore';
import {PlayTimeRange, StudentPlayerState, SyncedAudioState} from '../models';

export class TeacherSyncedAudioStore {
  readonly audio = new AudioElementStore();
  readonly progress = new AudioProgressStore();

  private readonly disposers = new Disposers();

  @observable.shallow
  readonly trackedStudents = new Map<number, AudioProgressStore>();

  constructor(readonly id: number,
              readonly model: SyncedAudioModel,
              readonly stateStore: X9ContentStateStore) {
    makeObservable(this);
    this.disposers.add(this.audio);
    this.disposers.add(this.progress);
    this.setup();
  }

  private setup() {
    const command = this.stateStore.getScopedContentState<PlayTimeRange>(this.id, 'command');
    if (command !== null && typeof command.commandId === 'number')
      this.audio.playCount = command.commandId;

    this.progress.syncAudioDurationFrom(this.audio);
    this.progress.syncCurrentTimeFrom(this.audio);
    this.progress.syncBufferedFrom(this.audio);

    this.disposers.add(reaction(
      () => [this.audio.playCount, this.audio.playFrom, this.audio.paused, this.audio.ended],
      this.sendCommands,
      {fireImmediately: true}
    ));
    this.disposers.add(reaction(
      () => this.stateStore.getContentState<SyncedAudioState>(this.id),
      this.trackStudents,
      {fireImmediately: true}
    ));
  }

  @action
  private sendCommands = () => {
    let command: PlayTimeRange;
    const audio = this.audio;
    if (audio.paused || audio.ended) {
      if (audio.currentTime === audio.playFrom)
        command = {commandId: audio.playCount, start: null, stop: null};
      else
        command = {commandId: audio.playCount, start: audio.playFrom, stop: audio.currentTime};
    }
    else
      command = {commandId: audio.playCount, start: audio.playFrom, stop: null};
    this.stateStore.setScopedContentState<PlayTimeRange>(this.id, 'command', command);
  };

  @computed
  private get syncedAudioStudents(): SyncedAudioState | null {
    const altSyncedAudioState: SyncedAudioState = { studentIds: [] }
    return this.stateStore.getContentState<SyncedAudioState>(this.id) ? this.stateStore.getContentState<SyncedAudioState>(this.id) : altSyncedAudioState;
  }

  @action
  private trackStudents = () => {
    const students = this.syncedAudioStudents;

    if (students === null) return;

    for (let studentId of students.studentIds) {
      if (this.trackedStudents.has(studentId))
        continue;
      const progress = new AudioProgressStore();
      progress.syncAudioDurationFrom(this.audio);
      const studentStateProvider =
        () => this.stateStore.getScopedContentState<StudentPlayerState>(this.id, studentId);
      progress.syncStudentState(studentStateProvider);
      this.trackedStudents.set(studentId, progress);
      this.disposers.add(progress);
    }
  }

  dispose() {
    this.disposers.dispose();
  }
}
